import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export type BillPayment = {
  id: number;
  businessId: number;
  billId: number;
  date: string; // Actually comes from payment_date in database
  amount: string; 
  paymentMethod: string;
  reference?: string; // Actually comes from reference_number in database
  notes?: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateBillPaymentData = {
  businessId?: number;
  billId: number;
  payment_date: Date | string; // Changed from date to payment_date to match database column
  amount: string;
  paymentMethod: string;
  reference_number?: string; // Changed from reference to reference_number to match database column
  notes?: string;
};

export function useBillPayments(billId?: number) {
  const { toast } = useToast();

  const { 
    data: payments = [], 
    isLoading, 
    isError,
    error,
    refetch 
  } = useQuery<BillPayment[], Error>({
    queryKey: ['/api/expenses/bill-payments', billId],
    queryFn: async () => {
      if (!billId) return [];
      
      const response = await apiRequest('GET', `/api/expenses/bill-payments?billId=${billId}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch bill payments');
      }
      
      return response.json() as Promise<BillPayment[]>;
    },
    enabled: !!billId,
  });

  const createBillPaymentMutation = useMutation({
    mutationFn: async (paymentData: CreateBillPaymentData) => {
      const { businessId, ...payload } = paymentData;
      const response = await apiRequest('POST', '/api/expenses/bill-payments', payload);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create bill payment');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/expenses/bill-payments', billId] });
      queryClient.invalidateQueries({ queryKey: ['/api/expenses/bills'] });
      toast({
        title: 'Success',
        description: 'Bill payment recorded successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    payments,
    isLoading,
    isError,
    error,
    refetch,
    createBillPaymentMutation
  };
}
